import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Alert, Box, Button, TextField } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { PersonType } from 'src/utils/constants/clients';
import { useDispatch, useSelector } from 'react-redux';
import {
  CLEAR_SANTANDER_CLABE_OPTIONS,
  loadSantanderClabes
} from 'src/store/actions/santanderClabeActions';
import SantanderClabeStatus from 'src/views/administrator/santanderClabes/santanderStatus.enum';
import { selectSantanderClabeList } from 'src/store/selectors/santanderClabeSelectors';
import useStyles from '../styles';
import validationSchema from './schema';
import makeInitialState from './initialState';

const getPersonTypeByRFC = rfc => {
  if (rfc) {
    if (rfc.length === 12) {
      return PersonType.LEGAL;
    }
    return PersonType.PHYSICAL;
  }
  return true;
};

const StepOne = ({ data, handleNext }) => {
  const { classes } = useStyles();
  const initialState = useMemo(() => makeInitialState(data), [data]);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const clabeList = useSelector(selectSantanderClabeList);

  const onNext = useCallback(
    async values => {
      const personType = getPersonTypeByRFC(values.rfc);
      handleNext({ ...values, personType });
    },
    [handleNext, data]
  );

  useEffect(() => {
    dispatch(
      loadSantanderClabes({ status: SantanderClabeStatus.UNASSIGNED }, false)
    );
    return () => {
      dispatch({ type: CLEAR_SANTANDER_CLABE_OPTIONS });
    };
  }, []);

  return (
    <>
      <Formik
        initialValues={initialState}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={onNext}
      >
        {({
          errors,
          handleSubmit,
          touched,
          values,
          handleChange,
          handleBlur
        }) => {
          return (
            <form onSubmit={handleSubmit}>
              <Box className={classes.container}>
                {!clabeList.length && (
                  <Alert severity="warning" sx={{ mt: 3 }}>
                    {t('COMMON.NO_CLABES_WARNING')}
                  </Alert>
                )}
                <TextField
                  InputProps={{
                    autoComplete: 'off'
                  }}
                  error={Boolean(touched.rfc && errors.rfc)}
                  fullWidth
                  helperText={touched.rfc && t(errors.rfc)}
                  label={t('COMMON.RFC')}
                  margin="normal"
                  name="rfc"
                  onBlur={handleBlur}
                  onChange={e => {
                    handleChange(e);
                  }}
                  onInput={e => {
                    e.target.value = e.target.value.toUpperCase();
                  }}
                  type="text"
                  value={values.rfc}
                  variant="outlined"
                />
              </Box>
              <Box
                className={classes.box}
                display="flex"
                justifyContent="flex-end"
              >
                <Button
                  className={clsx(classes.buttons, classes.buttonMain)}
                  type="submit"
                  color="primary"
                  variant="outlined"
                >
                  {t('COMMON.NEXT')}
                </Button>
              </Box>
            </form>
          );
        }}
      </Formik>
    </>
  );
};
StepOne.propTypes = {
  data: PropTypes.object,
  handleNext: PropTypes.func
};

export default StepOne;
